import { PropTypes } from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'

import { STRING } from 'resources'
import { APP_URLS } from 'resources/urls'

import IntercomBubble from 'components/IntercomBubble'

import { getRedemptionMinAccountBalance } from 'models/General/selectors'
import rewardsActions from 'models/Rewards/actions'
import { getGoalReward, getRewards } from 'models/Rewards/selectors'

import {
  Container,
  NavMenuItemsContainer,
  NavMenuItemLinkContainer,
  NavMenuItemIcon,
  NavMenuItemLabel,
  SubMenuItemsContainer,
  NavMenuSubItem,
  NavMenuBottomContainer,
  SignOutLink,
  SignOutText,
  SignOutIcon,
} from './styles'

const NavMenuContent = ({ closeMobileNavMenu = null }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const rewards = useSelector(getRewards)
  const minAccountBalance = useSelector(getRedemptionMinAccountBalance)
  const goalReward = useSelector(getGoalReward)

  const [activeHeadingByGoal, setActiveHeadingByGoal] = useState(null)

  const goalRewardId = goalReward?.id || null
  const enabledRewards = useMemo(() => new Set(rewards.map((reward) => reward.id)), [rewards])
  const paypalEnabled = enabledRewards.has('paypal')
  const venmoEnabled = enabledRewards.has('venmo')
  const sharesEnabled = enabledRewards.has('shares')

  const isHeadingActive = useCallback(
    (pathNames = [], goalId = null, categoryNames = []) =>
      pathNames.includes(location.pathname) &&
      (goalId ? goalId === goalRewardId : true) &&
      (categoryNames.length > 0 ? categoryNames.includes(location.state?.selectedCategory?.name) : true),
    [location.pathname, goalRewardId, location.state],
  )

  const setGoalAndNavigate = (goal) => {
    setActiveHeadingByGoal(goal)
    dispatch(
      rewardsActions.setRedemptionGoal.init(goal, minAccountBalance, (isSuccess) => {
        if (isSuccess) {
          dispatch(
            rewardsActions.fetchRedemptionGoal.init(() => {
              if (goal === 'shares') {
                history.push(APP_URLS.SHARES_FOR_REDEMPTION)
              } else {
                history.push(APP_URLS.WALLET)
              }
            }),
          )
        }
        closeMobileNavMenu?.()
      }),
    )
  }

  const navigateToProtect = () => {
    setActiveHeadingByGoal(null)
    history.push(APP_URLS.PROTECT)
    closeMobileNavMenu?.()
  }

  const navigateToStore = () => {
    setActiveHeadingByGoal(null)
    history.push(APP_URLS.STORE, { selectedCategory: null })
    closeMobileNavMenu?.()
  }

  const handleSharesClick = () => {
    setActiveHeadingByGoal(null)
    history.push(APP_URLS.SHARES_FOR_REDEMPTION)
    closeMobileNavMenu?.()
  }

  const protectHeadingActive = isHeadingActive([APP_URLS.PROTECT])
  const redeemHeadingActive = isHeadingActive([APP_URLS.STORE, APP_URLS.WALLET, APP_URLS.SHARES_FOR_REDEMPTION])
  const paypalHeadingActive =
    activeHeadingByGoal === 'paypal' || (isHeadingActive([APP_URLS.WALLET], 'paypal') && !activeHeadingByGoal)
  const venmoHeadingActive =
    activeHeadingByGoal === 'venmo' || (isHeadingActive([APP_URLS.WALLET], 'venmo') && !activeHeadingByGoal)
  const giftCardsHeadingActive =
    isHeadingActive([APP_URLS.STORE]) &&
    !isHeadingActive([APP_URLS.STORE], null, [STRING.redemptionStore.shares.categoryName, 'Cash'])
  const sharesHeadingActive =
    activeHeadingByGoal === 'shares' || (isHeadingActive([APP_URLS.SHARES_FOR_REDEMPTION]) && !activeHeadingByGoal)

  return (
    <Container>
      <NavMenuItemsContainer>
        <NavMenuItemLinkContainer
          to={APP_URLS.PROTECT}
          onClick={navigateToProtect}
          className={protectHeadingActive ? 'active' : ''}
        >
          <NavMenuItemIcon className="killi-icon-lock" />
          <NavMenuItemLabel>{STRING.navMenu.navMenuItems.reklaimProtect}</NavMenuItemLabel>
        </NavMenuItemLinkContainer>
        <NavMenuItemLinkContainer
          to={APP_URLS.STORE}
          onClick={navigateToStore}
          className={redeemHeadingActive ? 'active' : ''}
        >
          <NavMenuItemIcon className="killi-icon-wallet" />
          <NavMenuItemLabel>{STRING.navMenu.navMenuItems.redeem}</NavMenuItemLabel>
        </NavMenuItemLinkContainer>
        <SubMenuItemsContainer>
          {paypalEnabled && (
            <NavMenuSubItem
              onClick={() => setGoalAndNavigate('paypal')}
              className={paypalHeadingActive ? 'active' : ''}
            >
              <NavMenuItemLabel>{STRING.navMenu.navMenuItems.paypal}</NavMenuItemLabel>
            </NavMenuSubItem>
          )}
          {venmoEnabled && (
            <NavMenuSubItem onClick={() => setGoalAndNavigate('venmo')} className={venmoHeadingActive ? 'active' : ''}>
              <NavMenuItemLabel>{STRING.navMenu.navMenuItems.venmo}</NavMenuItemLabel>
            </NavMenuSubItem>
          )}
          <NavMenuSubItem onClick={navigateToStore} className={giftCardsHeadingActive ? 'active' : ''}>
            <NavMenuItemLabel>{STRING.navMenu.navMenuItems.giftcard}</NavMenuItemLabel>
          </NavMenuSubItem>
          {sharesEnabled && (
            <NavMenuSubItem onClick={handleSharesClick} className={sharesHeadingActive ? 'active' : ''}>
              <NavMenuItemLabel>{STRING.redemptionStore.shares.categoryName}</NavMenuItemLabel>
            </NavMenuSubItem>
          )}
        </SubMenuItemsContainer>
      </NavMenuItemsContainer>
      <NavMenuBottomContainer>
        <IntercomBubble />
        <SignOutLink to={APP_URLS.SIGN_OUT} onClick={closeMobileNavMenu}>
          <SignOutText>{STRING.navMenu.signOutButton}</SignOutText>
          <SignOutIcon />
        </SignOutLink>
      </NavMenuBottomContainer>
    </Container>
  )
}

NavMenuContent.propTypes = {
  closeMobileNavMenu: PropTypes.func,
}

export default NavMenuContent
